/**
 * @file PromotionsList.tsx
 * @description Колонки списка акций для `ResponsiveList` (phase18.3, promo.md
 * P1–P3; phase19.31 — каркас справочника (б) разбора B).
 *
 * От 1024 — таблица: Название · Скидка · Область · Канал · Период · Статус;
 * до 1024 — компактный список «Название … статус / скидка · область /
 * период». Статус — пилюлей по тону (идёт — зелёный, запланирована — синий,
 * завершена и выключена — серые, `PROMOTION_STATE_META`). Переключателя
 * «Активна» в строке нет — он дублировал статус; включение — в форме и в «⋯».
 */

import { StatusPill } from "../../../../components/ui/StatusPill";
import type { ListColumn } from "../../../../components/ui/responsive-list-types";
import type { CatalogCategory, CatalogPromotion } from "../../../../lib/api-catalog";
import { CHANNEL_LABELS, type SalesChannelKind } from "../../../../lib/api-channel-listings";
import { categoryLabel } from "../../../../lib/catalog-category-tree";
import { PROMOTION_STATE_META, promotionState } from "../../../../lib/catalog-status";
import { formatRub } from "../../../../lib/crm-deal-view";

/** «−10 %» / «−350,00 ₽». */
function valueLabel(p: CatalogPromotion): string {
  return p.kind === "PERCENT" ? `−${p.value} %` : `−${formatRub(p.value)}`;
}

function scopeLabel(p: CatalogPromotion, categoriesById: Map<string, CatalogCategory>): string {
  switch (p.scopeType) {
    case "CATALOG":
      return "Весь каталог";
    case "CATEGORY": {
      const category = p.categoryId ? categoriesById.get(p.categoryId) : undefined;
      return category ? categoryLabel(category) : "Категория";
    }
    case "PRODUCT":
      return "Один товар";
    case "SKU":
      return "Одна фасовка";
  }
}

function channelLabel(p: CatalogPromotion): string {
  return p.channel ? (CHANNEL_LABELS[p.channel as SalesChannelKind] ?? p.channel) : "Все каналы";
}

function periodLabel(p: CatalogPromotion): string {
  const fmt = (iso: string) => new Date(iso).toLocaleDateString("ru-RU");
  return p.endsAt ? `${fmt(p.startsAt)} — ${fmt(p.endsAt)}` : `с ${fmt(p.startsAt)}, бессрочно`;
}

export function buildPromotionColumns(categoriesById: Map<string, CatalogCategory>): ListColumn<CatalogPromotion>[] {
  return [
    { key: "name", header: "Название", cell: (p) => <span className="font-medium">{p.displayName}</span>, compact: "title" },
    { key: "value", header: "Скидка", width: "w-[120px]", cell: (p) => <span className="tabular-nums">{valueLabel(p)}</span>, compact: "subtitle" },
    { key: "scope", header: "Область", width: "w-[180px]", cell: (p) => scopeLabel(p, categoriesById), compact: "subtitle" },
    { key: "channel", header: "Канал", width: "w-[132px]", cell: channelLabel, fold: { into: "scope" }, compact: "meta" },
    { key: "period", header: "Период", width: "w-[200px]", cell: (p) => <span className="tabular-nums">{periodLabel(p)}</span>, compact: "meta" },
    { key: "state", header: "Статус", width: "w-[148px]", cell: (p) => <StatusPill meta={PROMOTION_STATE_META[promotionState(p)]} />, compact: "trailing" },
  ];
}
